<template>
  <div class="accompanist-profile">
    <!-- Header -->
    <div class="profile-header">
      <button class="back-button" @click="goBack">
        <span class="back-icon">←</span>
        Retour
      </button>
      <h1 class="page-title">Mon Profil</h1>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Chargement de votre profil...</p>
    </div>

    <!-- Profile Content -->
    <div v-else class="profile-content">
      <!-- Profile Card -->
      <div class="profile-card">
        <button class="edit-profile-icon-btn" @click="editMode = !editMode" :title="editMode ? 'Annuler' : 'Modifier mes informations'">
          <span class="edit-icon">✏️</span>
        </button>

        <div class="profile-avatar-section">
          <div class="avatar-wrapper">
            <img v-if="userProfile.has_picture" :src="profilePictureUrl" alt="Photo de profil" class="avatar-img" />
            <div v-else class="avatar-placeholder">
              {{ userProfile.first_name?.charAt(0) }}{{ userProfile.last_name?.charAt(0) }}
            </div>
          </div>
          <button class="edit-avatar-btn" @click="showUploadModal = true">
            <span class="camera-icon">📷</span>
            Changer la photo
          </button>
        </div>

        <div class="profile-info-section">
          <div class="info-header">
            <h2 class="profile-name">{{ userProfile.first_name }} {{ userProfile.last_name }}</h2>
            <span class="profile-role-badge">{{ getRoleName(userProfile.role_id) }}</span>
          </div>

          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Email</span>
              <span class="info-value">{{ userProfile.email }}</span>
            </div>

            <div class="info-item">
              <span class="info-label">Membre depuis</span>
              <span class="info-value">{{ formatDate(userProfile.date_created) }}</span>
            </div>

            <div class="info-item">
              <span class="info-label">Statut du compte</span>
              <span class="info-value">
                <span class="status-badge verified">✓ Vérifié</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Edit Form -->
      <div v-if="editMode" class="edit-form-card">
        <h3 class="form-title">Modifier mes informations</h3>
        <form @submit.prevent="saveProfile" class="edit-form">
          <div class="form-row">
            <div class="form-group">
              <label for="firstName" class="form-label">Prénom</label>
              <input
                id="firstName"
                type="text"
                v-model="editForm.first_name"
                class="form-input"
                required
              />
            </div>

            <div class="form-group">
              <label for="lastName" class="form-label">Nom</label>
              <input
                id="lastName"
                type="text"
                v-model="editForm.last_name"
                class="form-input"
                required
              />
            </div>
          </div>

          <div class="form-group">
            <label for="email" class="form-label">Email</label>
            <input
              id="email"
              type="email"
              v-model="editForm.email"
              class="form-input"
              required
            />
          </div>

          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <div v-if="successMessage" class="success-message">
            {{ successMessage }}
          </div>

          <div class="form-actions">
            <button type="button" class="btn-secondary" @click="cancelEdit">
              Annuler
            </button>
            <button type="submit" class="btn-primary" :disabled="saving">
              <span v-if="saving" class="btn-spinner"></span>
              {{ saving ? 'Enregistrement...' : 'Enregistrer' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Statistics Card -->
      <div class="stats-card">
        <h3 class="card-title">Mes statistiques</h3>
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-icon-wrapper" style="background: #DBEAFE;">
              <span class="stat-icon" style="color: #1E40AF;">👤</span>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ youngUsersCount }}</div>
              <div class="stat-label">Jeunes accompagnés</div>
            </div>
          </div>

          <div class="stat-item">
            <div class="stat-icon-wrapper" style="background: #D1FAE5;">
              <span class="stat-icon" style="color: #065F46;">📝</span>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ observationsCount }}</div>
              <div class="stat-label">Observations créées</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Security Card -->
      <div class="security-card">
        <h3 class="card-title">Sécurité</h3>
        <div class="security-section">
          <div class="security-item">
            <div class="security-info">
              <span class="security-label">Mot de passe</span>
              <span class="security-description">Dernière modification : {{ lastPasswordChange }}</span>
            </div>
            <button class="btn-link" @click="showPasswordModal = true">
              Changer le mot de passe
            </button>
          </div>
        </div>
      </div>

      <!-- Danger Zone -->
      <!-- <div class="danger-zone-card">
        <h3 class="card-title danger">Zone dangereuse</h3>
        <div class="danger-section">
          <div class="danger-item">
            <div class="danger-info">
              <span class="danger-label">Supprimer mon compte</span>
              <span class="danger-description">Cette action est irréversible</span>
            </div>
            <button class="btn-danger" @click="showDeleteModal = true">
              Supprimer mon compte
            </button>
          </div>
        </div>
      </div> -->
    </div>

    <!-- Upload Photo Modal -->
    <div v-if="showUploadModal" class="modal-overlay" @click="closeUploadModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Changer la photo de profil</h2>
          <button class="modal-close" @click="closeUploadModal">✕</button>
        </div>
        <div class="modal-body">
          <div class="upload-area">
            <input
              type="file"
              ref="fileInput"
              accept="image/*"
              @change="handleFileSelect"
              class="file-input"
            />
            <div class="upload-prompt">
              <span class="upload-icon">📁</span>
              <p>Cliquez pour sélectionner une image</p>
              <span class="upload-hint">JPG, PNG ou GIF (max 5MB)</span>
            </div>
          </div>
          <div v-if="selectedFile" class="selected-file">
            <span class="file-name">{{ selectedFile.name }}</span>
            <button class="remove-file" @click="removeFile">✕</button>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeUploadModal">Annuler</button>
          <button
            class="btn-primary"
            @click="uploadPhoto"
            :disabled="!selectedFile || uploading"
          >
            <span v-if="uploading" class="btn-spinner"></span>
            {{ uploading ? 'Upload...' : 'Télécharger' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Change Password Modal -->
    <div v-if="showPasswordModal" class="modal-overlay" @click="closePasswordModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Changer le mot de passe</h2>
          <button class="modal-close" @click="closePasswordModal">✕</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="changePassword" class="password-form">
            <div class="form-group">
              <label for="newPassword" class="form-label">Nouveau mot de passe</label>
              <input
                id="newPassword"
                type="password"
                v-model="passwordForm.new"
                class="form-input"
                placeholder="Minimum 8 caractères"
                required
              />
            </div>

            <div class="form-group">
              <label for="confirmPassword" class="form-label">Confirmer le mot de passe</label>
              <input
                id="confirmPassword"
                type="password"
                v-model="passwordForm.confirm"
                class="form-input"
                placeholder="Retapez votre mot de passe"
                required
              />
            </div>

            <div v-if="passwordError" class="error-message">
              {{ passwordError }}
            </div>

            <div v-if="passwordSuccess" class="success-message">
              {{ passwordSuccess }}
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closePasswordModal">Annuler</button>
          <button
            class="btn-primary"
            @click="changePassword"
            :disabled="changingPassword"
          >
            <span v-if="changingPassword" class="btn-spinner"></span>
            {{ changingPassword ? 'Modification...' : 'Changer le mot de passe' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Account Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Supprimer mon compte</h2>
          <button class="modal-close" @click="closeDeleteModal">✕</button>
        </div>
        <div class="modal-body">
          <div class="warning-box">
            <span class="warning-icon">⚠️</span>
            <p class="warning-text">
              <strong>Attention !</strong> Cette action est irréversible. Toutes vos données seront définitivement supprimées.
            </p>
          </div>
          <p class="confirmation-text">
            Pour confirmer, tapez "<strong>SUPPRIMER</strong>" dans le champ ci-dessous :
          </p>
          <input
            type="text"
            v-model="deleteConfirmation"
            class="form-input"
            placeholder="SUPPRIMER"
          />
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeDeleteModal">Annuler</button>
          <button
            class="btn-danger"
            @click="deleteAccount"
            :disabled="deleteConfirmation !== 'SUPPRIMER' || deleting"
          >
            <span v-if="deleting" class="btn-spinner"></span>
            {{ deleting ? 'Suppression...' : 'Supprimer définitivement' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService';
import accompanimentService from '@/services/accompaniment.service';

export default {
  name: 'AccompanistProfile',
  data() {
    return {
      loading: true,
      userProfile: {},
      editMode: false,
      editForm: {
        first_name: '',
        last_name: '',
        email: ''
      },
      saving: false,
      errorMessage: '',
      successMessage: '',

      // Stats
      youngUsersCount: 0,
      observationsCount: 0,

      // Modals
      showUploadModal: false,
      showPasswordModal: false,
      showDeleteModal: false,

      // Upload
      selectedFile: null,
      uploading: false,

      // Password
      passwordForm: {
        new: '',
        confirm: ''
      },
      changingPassword: false,
      passwordError: '',
      passwordSuccess: '',

      // Delete
      deleteConfirmation: '',
      deleting: false
    };
  },
  computed: {
    profilePictureUrl() {
      if (this.userProfile && this.userProfile.has_picture) {
        return `${process.env.VUE_APP_API_URL || 'http://localhost:8000'}/user/profile-picture`;
      }
      return '';
    },
    lastPasswordChange() {
      // TODO: Get from backend
      return 'Il y a 3 mois';
    }
  },
  async mounted() {
    await this.loadProfile();
    await this.loadStats();
  },
  methods: {
    async loadProfile() {
      try {
        this.loading = true;
        const response = await AuthService.request('get', '/user/profile');
        this.userProfile = response.data;

        // Populate edit form
        this.editForm = {
          first_name: this.userProfile.first_name,
          last_name: this.userProfile.last_name,
          email: this.userProfile.email
        };
      } catch (error) {
        console.error('Error loading profile:', error);
        this.errorMessage = 'Erreur lors du chargement du profil';
      } finally {
        this.loading = false;
      }
    },

    async loadStats() {
      try {
        const response = await accompanimentService.getYoungUsers();
        this.youngUsersCount = response.data.length;

        // TODO: Load observations count from backend
        this.observationsCount = 0;
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    },

    getRoleName(roleId) {
      const roles = {
        10: 'Enseignant',
        11: 'Psychologue',
        12: 'Famille',
        13: 'Expert'
      };
      return roles[roleId] || 'Accompagnant';
    },

    formatDate(dateString) {
      if (!dateString) return 'Non disponible';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    cancelEdit() {
      this.editMode = false;
      this.errorMessage = '';
      this.successMessage = '';
      // Reset form
      this.editForm = {
        first_name: this.userProfile.first_name,
        last_name: this.userProfile.last_name,
        email: this.userProfile.email
      };
    },

    async saveProfile() {
      try {
        this.saving = true;
        this.errorMessage = '';
        this.successMessage = '';

        await AuthService.request('put', '/user/profile', this.editForm);

        this.successMessage = 'Profil mis à jour avec succès !';
        await this.loadProfile();

        setTimeout(() => {
          this.editMode = false;
          this.successMessage = '';
        }, 2000);
      } catch (error) {
        console.error('Error saving profile:', error);
        this.errorMessage = error.response?.data?.detail || 'Erreur lors de la mise à jour du profil';
      } finally {
        this.saving = false;
      }
    },

    // Upload photo methods
    closeUploadModal() {
      this.showUploadModal = false;
      this.selectedFile = null;
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file && file.size <= 5 * 1024 * 1024) { // 5MB max
        this.selectedFile = file;
      } else {
        alert('Le fichier est trop volumineux (max 5MB)');
      }
    },

    removeFile() {
      this.selectedFile = null;
      this.$refs.fileInput.value = '';
    },

    async uploadPhoto() {
      if (!this.selectedFile) return;

      try {
        this.uploading = true;
        const formData = new FormData();
        formData.append('file', this.selectedFile);

        await AuthService.request('post', '/user/profile-picture', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        await this.loadProfile();
        this.closeUploadModal();
        alert('Photo de profil mise à jour avec succès !');
      } catch (error) {
        console.error('Error uploading photo:', error);
        alert('Erreur lors du téléchargement de la photo');
      } finally {
        this.uploading = false;
      }
    },

    // Password methods
    closePasswordModal() {
      this.showPasswordModal = false;
      this.passwordForm = { new: '', confirm: '' };
      this.passwordError = '';
      this.passwordSuccess = '';
    },

    async changePassword() {
      if (this.passwordForm.new !== this.passwordForm.confirm) {
        this.passwordError = 'Les mots de passe ne correspondent pas';
        return;
      }

      if (this.passwordForm.new.length < 8) {
        this.passwordError = 'Le mot de passe doit contenir au moins 8 caractères';
        return;
      }

      try {
        this.changingPassword = true;
        this.passwordError = '';
        this.passwordSuccess = '';

        // Étape 1 : Créer un token de réinitialisation pour l'utilisateur connecté
        const tokenResponse = await AuthService.request('get', '/recovery/create-account-recovery');
        const resetToken = tokenResponse.data.recover_code;

        // Étape 2 : Utiliser ce token pour réinitialiser le mot de passe
        await AuthService.resetPassword(this.passwordForm.new, resetToken);

        this.passwordSuccess = 'Mot de passe changé avec succès !';

        setTimeout(() => {
          this.closePasswordModal();
        }, 2000);
      } catch (error) {
        console.error('Error changing password:', error);
        this.passwordError = error.response?.data?.detail || 'Erreur lors du changement de mot de passe';
      } finally {
        this.changingPassword = false;
      }
    },

    // Delete account methods
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deleteConfirmation = '';
    },

    async deleteAccount() {
      if (this.deleteConfirmation !== 'SUPPRIMER') return;

      try {
        this.deleting = true;

        await AuthService.request('delete', '/user/account');

        // Logout and redirect
        AuthService.logout();
        this.$router.push('/commencer');
      } catch (error) {
        console.error('Error deleting account:', error);
        alert('Erreur lors de la suppression du compte');
      } finally {
        this.deleting = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'AccompanistDashboard' });
    }
  }
};
</script>

<style scoped>
.accompanist-profile {
  min-height: 100vh;
  background: linear-gradient(to bottom, #F9FAFB 0%, #FFFFFF 100%);
  padding: 2rem;
}

.profile-header {
  max-width: 1200px;
  margin: 0 auto 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: white;
  border: 2px solid #E5E7EB;
  border-radius: 12px;
  color: #374151;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-button:hover {
  background: #F9FAFB;
  border-color: #FF9800;
  color: #FF9800;
}

.back-icon {
  font-size: 1.25rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 800;
  color: #111827;
  margin: 0;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: #6B7280;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #E5E7EB;
  border-top-color: #FF9800;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.profile-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Profile Card */
.profile-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  position: relative;
}

.edit-profile-icon-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #FF9800;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
  z-index: 10;
}

.edit-profile-icon-btn:hover {
  background: #FF9800;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);
}

.edit-profile-icon-btn .edit-icon {
  font-size: 1.25rem;
}

.profile-avatar-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.avatar-wrapper {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid #E5E7EB;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
  font-weight: 700;
}

.edit-avatar-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.edit-avatar-btn:hover {
  background: #E5E7EB;
}

.camera-icon {
  font-size: 1.25rem;
}

.profile-info-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.profile-name {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.profile-role-badge {
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  color: white;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.info-value {
  font-size: 1rem;
  color: #111827;
  font-weight: 500;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
}

.status-badge.verified {
  background: #D1FAE5;
  color: #065F46;
}

.status-badge.unverified {
  background: #FEF3C7;
  color: #92400E;
}

/* Edit Form Card */
.edit-form-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 2px solid #FF9800;
}

.form-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 1.5rem 0;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  font-size: 1rem;
  color: #111827;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #FF9800;
  box-shadow: 0 0 0 3px rgba(255, 152, 0, 0.1);
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
}

.btn-secondary,
.btn-primary {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-secondary {
  background: #F3F4F6;
  color: #374151;
}

.btn-secondary:hover {
  background: #E5E7EB;
}

.btn-primary {
  background: #FF9800;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #FF9800;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.error-message {
  padding: 1rem;
  background: #FEE2E2;
  border: 1px solid #EF4444;
  border-radius: 8px;
  color: #991B1B;
  font-size: 0.875rem;
}

.success-message {
  padding: 1rem;
  background: #D1FAE5;
  border: 1px solid #10B981;
  border-radius: 8px;
  color: #065F46;
  font-size: 0.875rem;
}

/* Stats Card */
.stats-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.card-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 1.5rem 0;
}

.card-title.danger {
  color: #DC2626;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: #F9FAFB;
  border-radius: 12px;
}

.stat-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon {
  font-size: 2rem;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 2rem;
  font-weight: 800;
  color: #111827;
  line-height: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: #6B7280;
  margin-top: 0.25rem;
}

/* Security Card */
.security-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.security-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.security-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: #F9FAFB;
  border-radius: 12px;
  gap: 1rem;
  flex-wrap: wrap;
}

.security-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.security-label {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}

.security-description {
  font-size: 0.875rem;
  color: #6B7280;
}

.btn-link {
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid #FF9800;
  border-radius: 8px;
  color: #FF9800;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-link:hover {
  background: #FFF5F0;
}

/* Danger Zone Card */
.danger-zone-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 2px solid #FEE2E2;
}

.danger-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.danger-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: #FEF2F2;
  border-radius: 12px;
  gap: 1rem;
  flex-wrap: wrap;
}

.danger-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.danger-label {
  font-size: 1rem;
  font-weight: 600;
  color: #DC2626;
}

.danger-description {
  font-size: 0.875rem;
  color: #6B7280;
}

.btn-danger {
  padding: 0.5rem 1rem;
  background: #DC2626;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-danger:hover:not(:disabled) {
  background: #B91C1C;
}

.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #111827;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #9CA3AF;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: #F3F4F6;
  color: #111827;
}

.modal-body {
  margin-bottom: 1.5rem;
}

.modal-footer {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

/* Upload Area */
.upload-area {
  position: relative;
  border: 2px dashed #D1D5DB;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-area:hover {
  border-color: #FF9800;
  background: #F9FAFB;
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.upload-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.upload-icon {
  font-size: 3rem;
}

.upload-hint {
  font-size: 0.875rem;
  color: #6B7280;
}

.selected-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: #F3F4F6;
  border-radius: 8px;
  margin-top: 1rem;
}

.file-name {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
}

.remove-file {
  background: none;
  border: none;
  color: #DC2626;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.remove-file:hover {
  background: #FEE2E2;
}

/* Warning Box */
.warning-box {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: #FEF3C7;
  border: 1px solid #F59E0B;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.warning-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.warning-text {
  margin: 0;
  color: #92400E;
  font-size: 0.875rem;
}

.confirmation-text {
  color: #374151;
  margin-bottom: 1rem;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .accompanist-profile {
    padding: 1rem;
  }

  .profile-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .profile-card {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .profile-avatar-section {
    align-items: center;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .security-item,
  .danger-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .btn-link,
  .btn-danger {
    width: 100%;
    justify-content: center;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-secondary,
  .btn-primary {
    width: 100%;
    justify-content: center;
  }
}
</style>
